import React, { createContext, useCallback, useContext, useRef } from 'react';

/**
 * Universal Snapshot Flush Context
 * 
 * DESIGN PRINCIPLE (per ChatGPT recommendation):
 * "No dirty snapshot state survives a context change"
 * 
 * - ALL sections register flush handlers (not just Notes)
 * - flushAll() called synchronously on tab change, week change, unmount
 * - Debounce is for typing efficiency, flush is for correctness
 * - Section-agnostic: works for all weeks and all snapshot sections
 */

type FlushFn = () => Promise<void>;

interface FlushRegistration {
  section: string;
  periodKey: string;
  flush: FlushFn;
}

interface SnapshotFlushContextValue {
  /** Register a flush handler when section mounts */
  registerFlush: (section: string, periodKey: string, flush: FlushFn) => void;
  
  /** Unregister when section unmounts */
  unregisterFlush: (section: string, periodKey: string) => void;
  
  /** Flush ALL registered sections synchronously (awaits all) */
  flushAll: () => Promise<void>;
  
  /** Flush specific section */
  flushSection: (section: string) => Promise<void>;
}

const SnapshotFlushContext = createContext<SnapshotFlushContextValue | undefined>(undefined);

export const SnapshotFlushProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Map: `${section}:${periodKey}` -> flush function
  const flushHandlers = useRef<Map<string, FlushRegistration>>(new Map());

  const registerFlush = useCallback((section: string, periodKey: string, flush: FlushFn) => {
    const key = `${section}:${periodKey}`;
    flushHandlers.current.set(key, { section, periodKey, flush });
    
    console.log('[SnapshotFlush] Registered:', { section, periodKey, totalHandlers: flushHandlers.current.size });
  }, []);

  const unregisterFlush = useCallback((section: string, periodKey: string) => {
    const key = `${section}:${periodKey}`;
    flushHandlers.current.delete(key);
    
    console.log('[SnapshotFlush] Unregistered:', { section, periodKey, totalHandlers: flushHandlers.current.size });
  }, []);

  const flushAll = useCallback(async () => {
    const handlers = Array.from(flushHandlers.current.values());
    
    if (handlers.length === 0) {
      console.log('[SnapshotFlush] flushAll called, but no handlers registered');
      return;
    }

    console.log('[SnapshotFlush] Flushing ALL sections:', {
      count: handlers.length,
      sections: handlers.map(h => `${h.section}:${h.periodKey}`)
    });

    // Flush all in parallel (Promise.all)
    const startTime = Date.now();
    try {
      await Promise.all(handlers.map(h => h.flush()));
      const duration = Date.now() - startTime;
      
      console.log('[SnapshotFlush] All flushes completed', { 
        count: handlers.length,
        durationMs: duration
      });
    } catch (error) {
      console.error('[SnapshotFlush] Flush error:', error);
      // Don't throw - we want navigation to proceed even if flush fails
    }
  }, []);

  const flushSection = useCallback(async (section: string) => {
    const handler = Array.from(flushHandlers.current.values()).find(h => h.section === section);
    
    if (!handler) {
      console.log(`[SnapshotFlush] No handler for section: ${section}`);
      return;
    }

    console.log(`[SnapshotFlush] Flushing section: ${section}:${handler.periodKey}`);
    
    try {
      await handler.flush();
      console.log(`[SnapshotFlush] Section flushed: ${section}`);
    } catch (error) {
      console.error(`[SnapshotFlush] Section flush error (${section}):`, error);
    }
  }, []);

  const value: SnapshotFlushContextValue = React.useMemo(
    () => ({ registerFlush, unregisterFlush, flushAll, flushSection }),
    [registerFlush, unregisterFlush, flushAll, flushSection]
  );

  return (
    <SnapshotFlushContext.Provider value={value}>
      {children}
    </SnapshotFlushContext.Provider>
  );
};

export function useSnapshotFlush(): SnapshotFlushContextValue {
  const ctx = useContext(SnapshotFlushContext);
  if (!ctx) throw new Error('useSnapshotFlush must be used within SnapshotFlushProvider');
  return ctx;
}

export function useSnapshotFlushOptional(): SnapshotFlushContextValue | undefined {
  return useContext(SnapshotFlushContext);
}
